import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import axios from 'axios';
import { SERVER_URL } from '../../../../../Config/config';
import { toast } from 'react-hot-toast';

export const createAdmin = createAsyncThunk('admin/createAdmin', async (adminData, { rejectWithValue }) => {
  try {
    const response = await axios.post(`${SERVER_URL}/api/v1/admin/create`, adminData, {
      headers: { Authorization: `Bearer ${sessionStorage.getItem('accessToken')}` },
      withCredentials: true,
    });
    return response.data;
  } catch (err) {
    const errorMessage = err.response?.data?.message || 'Failed to create admin';
    toast.error(errorMessage);
    return rejectWithValue(errorMessage);
  }
});

export const getAdminList = createAsyncThunk('admin/getAdminList', async (_, { rejectWithValue }) => {
  try {
    const response = await axios.get(`${SERVER_URL}/api/v1/admin/list`, {
      headers: { Authorization: `Bearer ${sessionStorage.getItem('accessToken')}` },
      withCredentials: true,
    });
    return response.data;
  } catch (err) {
    const errorMessage = err.response?.data?.message || 'Failed to fetch admin list';
    toast.error(errorMessage);
    return rejectWithValue(errorMessage);
  }
});

export const deleteAdmin = createAsyncThunk('admin/deleteAdmin', async (adminId, { rejectWithValue }) => {
  try {
    const response = await axios.delete(`${SERVER_URL}/api/v1/admin/${adminId}`, {
      headers: { Authorization: `Bearer ${sessionStorage.getItem('accessToken')}` },
      withCredentials: true,
    });
    return response.data;
  } catch (err) {
    const errorMessage = err.response?.data?.message || 'Failed to delete admin';
    toast.error(errorMessage);
    return rejectWithValue(errorMessage);
  }
});

export const getAdminProfile = createAsyncThunk('admin/getAdminProfile', async (_, { rejectWithValue }) => {
  try {
    const response = await axios.get(`${SERVER_URL}/api/v1/admin/profile`, {
      headers: { Authorization: `Bearer ${sessionStorage.getItem('accessToken')}` },
      withCredentials: true,
    });
    return response.data;
  } catch (err) {
    const errorMessage = err.response?.data?.message || 'Failed to fetch profile';
    toast.error(errorMessage);
    return rejectWithValue(errorMessage);
  }
});

export const updateAdminProfile = createAsyncThunk('admin/updateAdminProfile', async (profileData, { rejectWithValue }) => {
  try {
    const response = await axios.put(`${SERVER_URL}/api/v1/admin/profile`, profileData, {
      headers: { Authorization: `Bearer ${sessionStorage.getItem('accessToken')}` },
      withCredentials: true,
    });
    return response.data;
  } catch (err) {
    const errorMessage = err.response?.data?.message || 'Failed to update profile';
    toast.error(errorMessage);
    return rejectWithValue(errorMessage);
  }
});

export const enable2FA = createAsyncThunk('admin/enable2FA', async (_, { rejectWithValue }) => {
  try {
    const response = await axios.post(`${SERVER_URL}/api/v1/admin/2fa/enable`, {}, {
      headers: { Authorization: `Bearer ${sessionStorage.getItem('accessToken')}` },
      withCredentials: true,
    });
    return response.data;
  } catch (err) {
    const errorMessage = err.response?.data?.message || 'Failed to enable 2FA';
    toast.error(errorMessage);
    return rejectWithValue(errorMessage);
  }
});

export const disable2FA = createAsyncThunk('admin/disable2FA', async (_, { rejectWithValue }) => {
  try {
    const response = await axios.post(`${SERVER_URL}/api/v1/admin/2fa/disable`, {}, {
      headers: { Authorization: `Bearer ${sessionStorage.getItem('accessToken')}` },
      withCredentials: true,
    });
    return response.data;
  } catch (err) {
    const errorMessage = err.response?.data?.message || 'Failed to disable 2FA';
    toast.error(errorMessage);
    return rejectWithValue(errorMessage);
  }
});

const adminAddSlice = createSlice({
  name: 'adminAdd',
  initialState: {
    admins: [],
    profile: null,
    loading: false,
    error: null,
    success: null,
  },
  reducers: {
    clearMessages: (state) => {
      state.error = null;
      state.success = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(createAdmin.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.success = null;
      })
      .addCase(createAdmin.fulfilled, (state, action) => {
        state.loading = false;
        state.success = action.payload.message;
        state.admins.push(action.payload.admin);
      })
      .addCase(createAdmin.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(getAdminList.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getAdminList.fulfilled, (state, action) => {
        state.loading = false;
        state.admins = action.payload.admins;
      })
      .addCase(getAdminList.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(deleteAdmin.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.success = null;
      })
      .addCase(deleteAdmin.fulfilled, (state, action) => {
        state.loading = false;
        state.success = action.payload.message;
        state.admins = state.admins.filter((admin) => admin.id !== action.meta.arg);
      })
      .addCase(deleteAdmin.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(getAdminProfile.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getAdminProfile.fulfilled, (state, action) => {
        state.loading = false;
        state.profile = action.payload.admin;
      })
      .addCase(getAdminProfile.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(updateAdminProfile.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.success = null;
      })
      .addCase(updateAdminProfile.fulfilled, (state, action) => {
        state.loading = false;
        state.success = action.payload.message;
        state.profile = action.payload.admin;
      })
      .addCase(updateAdminProfile.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(enable2FA.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.success = null;
      })
      .addCase(enable2FA.fulfilled, (state, action) => {
        state.loading = false;
        state.success = action.payload.message;
        if (action.payload.admin) {
          state.profile = state.profile
            ? { ...state.profile, ...action.payload.admin, twoFactorEnabled: true }
            : { ...action.payload.admin, twoFactorEnabled: true };
        }
      })
      .addCase(enable2FA.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(disable2FA.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.success = null;
      })
      .addCase(disable2FA.fulfilled, (state, action) => {
        state.loading = false;
        state.success = action.payload.message;
        if (action.payload.admin) {
          state.profile = state.profile
            ? { ...state.profile, ...action.payload.admin, twoFactorEnabled: false }
            : { ...action.payload.admin, twoFactorEnabled: false };
        }
      })
      .addCase(disable2FA.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });
  },
});

export const { clearMessages } = adminAddSlice.actions;
export default adminAddSlice.reducer;